import { createContext, useContext, useEffect } from 'react'
import {
  DraftQuestionnaireEntry,
  EntryType
} from '../../data/model/questionnaire'
import type { MarketAudienceIndex } from './audienceDisplayLogic'

export type MarketAudienceStatus = 'loading' | 'ready' | 'failed'

export interface MarketAudience {
  status: MarketAudienceStatus
  index: MarketAudienceIndex
  marketLabels: Map<string, string>
  entries: DraftQuestionnaireEntry<EntryType.QuestionEntryType>[]
}

export const emptyMarketAudience: MarketAudience = {
  status: 'ready',
  index: { marketIds: [], questions: new Map() },
  marketLabels: new Map(),
  entries: []
}

export const MarketAudienceContext = createContext<{
  marketAudience: MarketAudience
  request: () => void
} | null>(null)

/**
 * Every market's audience, fetched once per questionnaire page on the first read
 * that isn't skipped; empty in a project without markets and until they resolve.
 * `status` tells a pending or failed query from a resolved one.
 */
export const useMarketAudience = ({
  skip = false
}: { skip?: boolean } = {}): MarketAudience => {
  const context = useContext(MarketAudienceContext)
  const request = context?.request

  useEffect(() => {
    if (!skip) {
      request?.()
    }
  }, [skip, request])

  return context?.marketAudience ?? emptyMarketAudience
}
